import type { Country } from '@/data/types';
import { COUNTRIES } from '@/data';
import { WORKERS } from '@/data/workers';
import { workerCountries } from '@shared/roster';
import { isSuperAdmin } from '@/config/admins';

/**
 * Per-user access rules.
 *
 *  - Super admins see every jurisdiction and can switch freely.
 *  - A roster member is locked to their own country (France people only see
 *    France, Spain only Spain, Portugal only Portugal). They cannot navigate
 *    to another jurisdiction.
 *  - A signed-in @lexidy.com viewer who is not yet on the roster has no assigned
 *    country. Until the admin approval + country-assignment flow is wired on the
 *    server they keep the onboarding picker (unrestricted). See AdminView.
 */

export function workerFor(email: string | null | undefined) {
  if (!email) return null;
  const e = email.trim().toLowerCase();
  return WORKERS.find((w) => w.email?.toLowerCase() === e) ?? null;
}

/** Countries this user is allowed to view.
 *  @param assignedCountry country granted by the admin approval flow (for
 *         viewers who are not on the roster). */
export function allowedCountries(
  email: string | null | undefined,
  assignedCountry?: string | null,
  isSuper?: boolean,
): Country[] {
  if (isSuper || isSuperAdmin(email)) return COUNTRIES;
  const w = workerFor(email);
  if (w) {
    // Members see every jurisdiction they work in (e.g. Eva: FR + ES).
    const codes = workerCountries(w);
    const cs = COUNTRIES.filter((co) => codes.some((cc) => cc === co.code));
    return cs.length ? cs : COUNTRIES;
  }
  if (assignedCountry) {
    const c = COUNTRIES.find((co) => co.code === assignedCountry.toUpperCase());
    if (c) return [c];
  }
  return COUNTRIES; // viewer not yet on the roster — pending approval flow
}

/** The Planner (Planificador Rocks) — Spain's Recurring (payroll) team and
 *  super admins only. Everyone else never sees the sidebar entry. */
export function canUsePlanner(email: string | null | undefined, isSuper?: boolean): boolean {
  if (isSuper || isSuperAdmin(email)) return true;
  const w = workerFor(email);
  return !!w && w.department === 'Recurring' && w.country === 'ES';
}

/** Jurisdictions whose "My work" (tickets + tasks) is suspended.
 *  Nobody's, today: PT's own pipelines (908000417, 920226306, 22363756,
 *  28258379 + the shared 9885712) were mapped all along — the gate was the
 *  only thing between the team and their board. IT and MX run live too. */
export const SUSPENDED_MYWORK: readonly string[] = [];

/** True when "My work" is not yet available for the given country code. */
export function isMyWorkSuspended(countryCode: string): boolean {
  return SUSPENDED_MYWORK.includes(countryCode);
}
